import { Link } from '@innovationnorway/component-library';
import { FC, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApplicationCard } from '../../components/ApplicationCard/ApplicationCard';
import { CustomerTaskList } from '../../components/CustomerTaskList/CustomerTaskList';
import { DocumentList } from '../../components/DocumentList/DocumentList';
import { TabDefinition, Tabs } from '../../components/Shared/Tabs/Tabs';
import { useLocalizer } from '../../hooks';
import { useEnvironment } from '../../hooks/useEnvironment';
import { createRedirectForSubmittedView, useFormActionHandler } from '../../hooks/useFormActionHandler';
import { apiClient } from '../../lib/query/apiClient';
import { ApplicationDto, CustomerTaskDto, FormActionDto, FormActionTypeEnum, FormApplicationTypeEnum } from '../../models';
import { saveDocument } from '../../services/documents.service';

type ApplicationDetailsDataParams = {
  applicationId: string;
};

const tabTypes = ['tasks', 'documents', 'history'] as const;
type TabType = (typeof tabTypes)[number];

export const ApplicationDetailsData: FC = () => {
  const [applicationFormAction, setApplicationFormAction] = useState<FormActionDto>({} as FormActionDto);

  const { applicationId } = useParams() as ApplicationDetailsDataParams;

  const [searchParams, setSearchParams] = useSearchParams({ tab: 'tasks' });
  // The 'tab' search param is the single source of truth for the active tab.
  const currentTab = (searchParams.get('tab') as TabType | null) ?? 'tasks';
  const selectedIndex = Math.max(0, tabTypes.indexOf(currentTab));

  const { l } = useLocalizer('ApplicationDetails');
  const formActionHandler = useFormActionHandler();
  const { caseHelpUrl } = useEnvironment();

  const { data: application } = apiClient.useSuspenseQuery('get', '/api/v1/Applications/{applicationId}', {
    params: { path: { applicationId } },
  });

  const { data: customerTasksData } = apiClient.useSuspenseQuery('get', `/api/v1/Applications/{applicationId}/customer-tasks`, {
    params: { path: { applicationId } },
  });

  const { data: documents } = apiClient.useSuspenseQuery('get', `/api/v1/Documents`, {
    params: { query: { context: applicationId } },
  });

  const tasks = customerTasksData?.tasks ?? [];
  // Derived directly from the fetched data — no state or effect needed.
  const tabs: TabDefinition[] = [
    {
      id: 0,
      label: `${l('taskTabLabel')} (${tasks.filter((t) => t.active).length})`,
      title: `${l('taskTabLabel')}`,
    },
    {
      id: 1,
      label: `${l('documentTabLabel')} (${documents.length})`,
      title: l('documentTabLabel'),
    },
    {
      id: 2,
      label: `${l('historyTabLabel')} (1)`,
      title: `${l('historyTabLabel')}`,
    },
  ];

  const handleTabOnSelect = (index: number): void => {
    const nextTab = tabTypes[index];
    if (!nextTab || nextTab === currentTab) return;
    setSearchParams({ tab: nextTab }, { replace: true });
  };

  useEffect(() => {
    if (application.id && application.customerAccountId) {
      const isConferenceAttendance = application.applicationType?.shortName === 'conference-attendance';

      const shouldUseLocalSubmittedView = isConferenceAttendance || customerTasksData?.maintenanceFormUrl;

      const baseUrl = shouldUseLocalSubmittedView ? `/forms/submitted/` : caseHelpUrl;

      const actionUrl = `${baseUrl}${application.id}|${application.customerAccountId}`;

      const submitted = createRedirectForSubmittedView(application.decisionSystemApplicationType?.shortName, `${application.id}|${application.customerAccountId}`);

      if (submitted) {
        setApplicationFormAction(submitted);
      } else {
        setApplicationFormAction({
          data: actionUrl,
          applicationType: FormApplicationTypeEnum.None,
          actionType: FormActionTypeEnum.CaseRedirect,
        } as FormActionDto);
      }
    }
  }, [application, caseHelpUrl, customerTasksData?.maintenanceFormUrl]);

  return (
    <div>
      <ApplicationCard application={application as ApplicationDto} />
      <Tabs aria-label={l('menuAriaLabel')} tabs={tabs} handleTabOnSelect={handleTabOnSelect} overrideSelection={selectedIndex} />
      {currentTab === 'history' && (
        <div>
          <div className="heading mt-8 mb-0 font-bold">
            <span className=" bg-gray-200 font-medium ml-0.5">&nbsp;&nbsp;1&nbsp;&nbsp;</span>
            &nbsp;{l('historyTitle')}
          </div>
          <div className="pl-5 ml-4 border-l-2 border-gray-300">
            <div className="textBox mt-1">
              <p className="text mt-0 pt-4">{l('linkToCaseLeadText')}</p>
            </div>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                history.pushState({}, document.title, window.location.href);
                formActionHandler(applicationFormAction);
              }}
              iconName="fal fa-arrow-right"
              iconPosition="right"
              hideUnderline={false}
            >
              {l('linkToCaseLinkText')}
            </Link>
          </div>
        </div>
      )}
      {currentTab === 'tasks' && <CustomerTaskList tasks={(customerTasksData?.tasks as CustomerTaskDto[]) ?? []} isLoaded={true} />}
      {currentTab === 'documents' && <DocumentList documents={documents} onDownload={saveDocument} isLoaded={true} />}
    </div>
  );
};
